import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CommonLayout from './CommonLayout';
import moment from 'moment';
import ButtonSecondary from 'shared/Button/ButtonSecondary';
import Badge from 'shared/Badge/Badge';
import { useOrders } from '../../hooks/collections/useOrdersV2';
import { BadgeOrder } from '../../components/OrderStatus/BadgeOrder';
import QuillRenderer from '../../@core/components/QuillRenderer';
import { useReservations } from '../../hooks/collections/useReservations';
import { Collections } from '../../@core/constants/FirebasePaths';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { getOrder, getReference } = useOrders({ loadData: false });
  const { reservations } = useReservations({
    loadData: true, queries: [
      { type: 'where', field: 'order', operator: '==', value: getReference(`${Collections.orders._path}/${orderId}`) }
    ]
  });

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      try {
        const order = await getOrder(orderId);
        if (order) {
          setOrder(order);
          return;
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) fetchOrder();
  }, [orderId]);

  const renderOrderHeader = () => {
    if (!order) return null;

    return (
      <div className="listingSection__wrap !space-y-6">
        <div className="flex justify-between items-center">
          <Badge name={`Order #${order?.orderId}`}/>
          <div><BadgeOrder status={order.status}/></div>
        </div>

        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
          {order.service.title}
        </h2>

        <div className="flex items-center space-x-4">
          <span>
            <i className="las la-calendar-alt"></i>
            <span className="ml-1">Reservation(s) for: </span>

            {reservations?.length && reservations.map((reservation, index) => (
              <span key={reservation.id}>
    {moment(reservation.date).format('MMMM D, YYYY h:mm A')}
                {index < reservations.length - 1 && ', '}
  </span>
            ))}
          </span>
        </div>

        <div className="w-full border-b border-neutral-200 dark:border-neutral-700"/>
      </div>
    );
  };

  const renderOrderDetails = () => {
    if (!order) return null;

    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Order Details</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-4"></div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Service Information</h3>
              <p className="text-neutral-500 dark:text-neutral-400">{order.service.title}</p>
              {order.service.description && (
                <div className="mt-2">
                  <QuillRenderer
                    content={order.service.description}
                    className="text-neutral-500 dark:text-neutral-400"
                  />
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium">Total</h3>
              <p className="text-neutral-500 dark:text-neutral-400">${order.total}</p>
            </div>

            <div>
              <h3 className="text-lg font-medium">Order placed on</h3>
              <p className="text-neutral-500 dark:text-neutral-400">
                {moment(order.createdAt).format('MMMM D, YYYY')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Location</h3>
              <p className="text-neutral-500 dark:text-neutral-400">
                {order.service.details.location.address}
              </p>
            </div>

            {order.createdBy && (
              <div>
                <h3 className="text-lg font-medium">Customer</h3>
                <p className="text-neutral-500 dark:text-neutral-400">
                  {order.createdBy.name}<br/>
                  {order.createdBy.email}<br/>
                  {order.createdBy.phone}
                </p>
              </div>
            )}

            {order.notes && (
              <div>
                <h3 className="text-lg font-medium">Notes</h3>
                <p className="text-neutral-500 dark:text-neutral-400">{order.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderActionButtons = () => {
    return (
      <div className="listingSection__wrap">
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <ButtonSecondary onClick={() => navigate(-1)}>
            <i className="las la-arrow-left mr-2"></i>
            Back to Orders
          </ButtonSecondary>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <CommonLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </CommonLayout>
    );
  }

  if (!order) {
    return (
      <CommonLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <h2 className="text-2xl font-semibold text-neutral-700 dark:text-neutral-200">Order not found</h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">The order you're looking for doesn't exist or you
            don't have permission to view it.</p>
          <ButtonSecondary className="mt-4" onClick={() => navigate(-1)}>
            Back to Orders
          </ButtonSecondary>
        </div>
      </CommonLayout>
    );
  }

  return (
    <div>
      <CommonLayout>
        <div className="space-y-8">
          {renderOrderHeader()}
          {renderOrderDetails()}
          {renderActionButtons()}
        </div>
      </CommonLayout>
    </div>
  );
};

export default OrderDetailPage;
